// ============================================================
// Halaman Detail Pesanan — shop order
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

const STATUS_LABELS = {
  pending: 'Pending',
  diproses: 'Diproses',
  desain: 'Desain',
  printing: 'Printing',
  finishing: 'Finishing',
  dikirim: 'Dikirim',
  selesai: 'Selesai',
  dibatalkan: 'Dibatalkan',
};

const STATUS_BADGES = {
  pending: 'badge-gray',
  diproses: 'badge-yellow',
  desain: 'badge-blue',
  printing: 'badge-blue',
  finishing: 'badge-blue',
  dikirim: 'badge-orange',
  selesai: 'badge-green',
  dibatalkan: 'badge-red',
};

const PAY_LABELS = {
  unpaid: 'Belum Bayar',
  pending: 'Pending',
  pending_confirmation: 'Konfirmasi',
  paid: 'Lunas',
  failed: 'Gagal',
  refunded: 'Refund',
};

const PAY_BADGES = {
  unpaid: 'badge-red',
  pending: 'badge-yellow',
  pending_confirmation: 'badge-orange',
  paid: 'badge-green',
  failed: 'badge-red',
  refunded: 'badge-gray',
};

function createKasirOrderDetail(config = {}) {
  return {
    orderId: config.orderId || '',
    indexUrl: config.indexUrl || '',
    posUrl: config.posUrl || '',

    loading: true,
    error: null,
    order: null,

    newStatus: '',
    statusNote: '',
    statusLoading: false,

    showConfirmModal: false,
    confirmNote: '',
    confirmLoading: false,
    _blockOutside: false,

    showNotifyModal: false,
    notifyTitle: '',
    notifyBody: '',
    notifyPush: true,
    notifyLoading: false,

    async init() {
      await this.load();
    },

    async load() {
      if (!this.orderId) {
        this.error = 'ID pesanan tidak valid.';
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/v1/orders/' + encodeURIComponent(this.orderId));
        this.order = data.data || null;
        if (!this.order) {
          this.error = 'Pesanan tidak ditemukan.';
        } else {
          this.newStatus = '';
        }
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal memuat detail pesanan.';
        this.order = null;
      } finally {
        this.loading = false;
      }
    },

    customerName() {
      return this.order?.user?.name || this.order?.customer_name || '-';
    },

    customerPhone() {
      return this.order?.user?.phone || this.order?.customer_phone || '-';
    },

    shippingText() {
      const formatted = this.order?.shipping_address_formatted;
      if (formatted?.text) return formatted.text;
      const addr = this.order?.shipping_address;
      if (!addr) return this.order?.shipping_method || '—';
      if (typeof addr === 'string') return addr;
      if (addr.full_address) {
        return [addr.recipient_name || addr.recipient, addr.phone, addr.full_address]
          .filter(Boolean)
          .join('\n');
      }
      const parts = [
        addr.recipient_name || addr.recipient,
        addr.phone,
        addr.address_line || addr.address,
        addr.city,
        addr.province,
        addr.postal_code,
      ].filter(Boolean);
      return parts.length ? parts.join('\n') : (this.order?.shipping_method || '—');
    },

    openConfirmModal() {
      this._blockOutside = true;
      this.confirmNote = '';
      this.showConfirmModal = true;
      this.$nextTick(() => {
        setTimeout(() => { this._blockOutside = false; }, 80);
      });
    },

    async submitConfirm() {
      if (!this.order?.id) return;
      this.confirmLoading = true;
      try {
        await axios.post('/api/v1/orders/' + this.order.id + '/confirm-payment', {
          note: this.confirmNote,
        });
        window.toast('Pembayaran dikonfirmasi.', 'success');
        this.showConfirmModal = false;
        await this.load();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal konfirmasi.', 'error');
      } finally {
        this.confirmLoading = false;
      }
    },

    openNotifyModal() {
      this._blockOutside = true;
      this.notifyTitle = 'Update pesanan ' + (this.order?.order_number || '');
      this.notifyBody = '';
      this.notifyPush = true;
      this.showNotifyModal = true;
      this.$nextTick(() => {
        setTimeout(() => { this._blockOutside = false; }, 80);
      });
    },

    closeNotifyModal() {
      if (this._blockOutside) return;
      this.showNotifyModal = false;
    },

    async submitNotify() {
      if (!this.order?.id) return;
      if (!this.notifyTitle.trim() || !this.notifyBody.trim()) {
        window.toast('Judul dan pesan wajib diisi.', 'warning');
        return;
      }
      this.notifyLoading = true;
      try {
        await axios.post('/api/v1/orders/' + this.order.id + '/notify', {
          title: this.notifyTitle.trim(),
          body: this.notifyBody.trim(),
          send_push: !!this.notifyPush,
        });
        window.toast('Notifikasi dikirim ke customer.', 'success');
        this._blockOutside = false;
        this.showNotifyModal = false;
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal mengirim notifikasi.', 'error');
      } finally {
        this.notifyLoading = false;
      }
    },

    async updateStatus() {
      if (!this.order?.id || !this.newStatus) return;
      this.statusLoading = true;
      try {
        await axios.post('/api/v1/orders/' + this.order.id + '/status', {
          status: this.newStatus,
          note: this.statusNote || undefined,
        });
        window.toast('Status diperbarui: ' + this.statusLabel(this.newStatus), 'success');
        this.statusNote = '';
        await this.load();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal update status.', 'error');
        this.newStatus = '';
      } finally {
        this.statusLoading = false;
      }
    },

    processToPos() {
      if (!this.posUrl || !this.order?.id) return;
      window.location.href = this.posUrl + '?source=online&order_id=' + this.order.id;
    },

    downloadInvoice() {
      if (!this.order?.order_number) return;
      window.open('/invoices/' + this.order.order_number + '/download', '_blank');
    },

    printSpk() {
      if (this.order?.spk_print_url) {
        window.open(this.order.spk_print_url, '_blank');
      } else {
        window.toast('SPK belum tersedia.', 'warning');
      }
    },

    printSpkInvoice() {
      if (this.order?.spk_invoice_print_url) {
        window.open(this.order.spk_invoice_print_url, '_blank');
      } else {
        window.toast('Invoice SPK belum tersedia.', 'warning');
      }
    },

    statusLabel(s) {
      return STATUS_LABELS[s] || s || '—';
    },

    statusBadge(s) {
      return STATUS_BADGES[s] || 'badge-gray';
    },

    payLabel(s) {
      return PAY_LABELS[s] || s || '—';
    },

    payBadge(s) {
      return PAY_BADGES[s] || 'badge-gray';
    },

    fmtRP(n) {
      return Number(n || 0).toLocaleString('id-ID');
    },

    fmtDate(d) {
      if (!d) return '-';
      // API may return already-formatted string
      if (typeof d === 'string' && !d.includes('T') && d.length < 22) return d;
      return new Date(d).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  };
}

Alpine.data('kasirOrderDetail', (config = {}) => createKasirOrderDetail(config));
